import Constants from 'expo-constants';
import { BaseGooglePlaces } from '@domain/addresses/blocs/services/base-google-places';
import { Loader } from '@googlemaps/js-api-loader';

export enum PlacesServiceStatus {
  OK = 'OK',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  ZERO_RESULTS = 'ZERO_RESULTS',
}

export class GooglePlaces extends BaseGooglePlaces {
  static _services: {
    autocomplete: google.maps.places.AutocompleteService;
    places: google.maps.places.PlacesService;
  };

  static get services() {
    return (async () => {
      if (!this._services) {
        const loader = new Loader({
          apiKey: Constants.expoConfig!.extra!.googleMaps.apiKey,
          libraries: ['places'],
          version: 'quarterly',
        });

        const places = await loader.importLibrary('places');
        this._services = {
          autocomplete: new places.AutocompleteService(),
          places: new places.PlacesService(
            // PlacesService requires a DOM element but it doesn't have to do
            // anything so we create a placeholder div
            document.createElement('div'),
          ),
        };
      }

      return this._services;
    })();
  }

  static getPlaceAutocomplete = async (
    input: string,
    types?: string[],
    locationBias?: google.maps.LatLngBoundsLiteral,
  ): Promise<google.maps.places.AutocompletePrediction[]> => {
    const service = (await GooglePlaces.services).autocomplete;
    return new Promise((resolve, reject) => {
      service.getPlacePredictions(
        {
          bounds: locationBias,
          input,
          types,
        },
        (result, status) => {
          switch (status) {
            case PlacesServiceStatus.OK:
              return resolve(result || []);
            case PlacesServiceStatus.ZERO_RESULTS:
              return resolve([]);
          }

          return reject(new Error(status));
        },
      );
    });
  };

  static getPlaceDetails = async (
    placeID: string,
  ): Promise<google.maps.places.PlaceResult> => {
    const service = (await GooglePlaces.services).places;
    return new Promise((resolve, reject) => {
      service.getDetails(
        {
          fields: [
            'address_components',
            'formatted_address',
            'geometry',
            'name',
            'types',
          ],
          placeId: placeID,
        },
        (result, status) => {
          if (status === PlacesServiceStatus.OK && result) {
            resolve(result);
          }

          return reject(new Error(status));
        },
      );
    });
  };
}
